import { useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import Router from "./routes/Routes";
import { ApplicationContext } from "./context/GlobalState";
import {
  setBodyOverflow,
  setMobileMenuOpen,
  setPrefersReducedMotion,
  setPwaMode,
} from "./context/actions";

/**
 * Main application shell for global effects and route rendering.
 * Translation: Main application shell for global effects and route rendering.
 *
 * @returns {JSX.Element} App root.
 */
const App = () => {
  const [state, dispatch] = useContext(ApplicationContext);
  const location = useLocation();
  const didScrollRef = useRef(false);
  const [isAuthenticating, setIsAuthenticating] = useState(true);

  useEffect(() => {
    setIsAuthenticating(false);
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

    /**
     * Sync reduced motion preferences from the operating system.
     * Translation: Sync reduced motion preferences from the operating system.
     */
    const syncReducedMotion = () => {
      dispatch(setPrefersReducedMotion(mediaQuery.matches));
    };

    syncReducedMotion();
    mediaQuery.addEventListener("change", syncReducedMotion);

    return () => mediaQuery.removeEventListener("change", syncReducedMotion);
  }, [dispatch]);

  useEffect(() => {
    let rafId = null;

    const handleGlobalMouseMove = (e) => {
      if (rafId) return;
      const x = e.clientX;
      rafId = requestAnimationFrame(() => {
        document.documentElement.style.setProperty("--mouse-x", `${x}px`);
        document.documentElement.style.setProperty("--glow-opacity", "1");
        rafId = null;
      });
    };

    const handleGlobalMouseLeave = () => {
      document.documentElement.style.setProperty("--glow-opacity", "0");
    };

    window.addEventListener("mousemove", handleGlobalMouseMove, { passive: true });
    window.addEventListener("mouseleave", handleGlobalMouseLeave);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener("mousemove", handleGlobalMouseMove);
      window.removeEventListener("mouseleave", handleGlobalMouseLeave);
    };
  }, []);

  useEffect(() => {
    dispatch(setPwaMode("manifest-only"));
  }, [dispatch]);

  useEffect(() => {
    const nextOverflow =
      state.isMobileMenuOpen || state.isModalOpen ? "hidden" : "auto";

    dispatch(setBodyOverflow(nextOverflow));
  }, [dispatch, state.isMobileMenuOpen, state.isModalOpen]);

  useEffect(() => {
    document.body.classList.remove("overflow-hidden");
    document.body.style.overflow = state.bodyOverflow;

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [state.bodyOverflow]);

  useEffect(() => {
    dispatch(setMobileMenuOpen(false));
    dispatch(setBodyOverflow("auto"));

    if (location.hash) {
      const elementId = location.hash.replace("#", "");
      const targetElement = document.getElementById(elementId);

      if (targetElement) {
        window.setTimeout(() => {
          targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 20);
      }

      return;
    }

    if (!didScrollRef.current) {
      window.scrollTo({ top: 0, behavior: "auto" });
      didScrollRef.current = true;
      return;
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [dispatch, location.hash, location.pathname]);

  return (
    !isAuthenticating && (
      <div className="App">
        <Router />
      </div>
    )
  );
};

export default App;
